import { EWFStepType, IScreenDesignInfo, IWFNodes, IWFStep, IWorkFlow } from "@kuika/kuika-cl-model";
import _ from "lodash";
import React from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInfoBox from "../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaMenuItem from "../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaPopover from "../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { IPendingUnmanagedAssignment } from "./step-screen-form-wizard-assign-screen-dropdown";
import "./step-screen-form-wizard-sidebar.scss";
import { StepScreenModalUnlinkScreenModal } from "./step-screen-form-wizard-unlink-screen-modal";
import { IStepScreenStateIcon, STEP_SCREEN_STATE_ICON, StepScreenState } from "./step-screen-state-icon";

export interface IStepScreenFormSidebarProps {
  processes: IWorkFlow[];
  selectedProcessId: string | null;
  selectedStepId: string | null;
  /** Both null clears the selection — the rail's "Get Started" row. */
  onChangeSelectedStep: (processId: string | null, stepId: string | null) => void;
  onClickGetStarted?: () => void;
  getScreenForStep: (processId: string, stepId: string) => IScreenDesignInfo | undefined;
  getIsScreenManaged: (processId: string, stepId: string) => boolean;
  draftSteps: { processId: string; stepId: string }[];
  pendingUnmanagedAssignments?: IPendingUnmanagedAssignment[];
  reloadScreen: () => Promise<void>;
  removeDraftFormMetadata: (processId: string, stepId: string) => void;
  setScreenUnmanaged: (processId: string, stepId: string) => void;
  /** Workflow ids whose process takes input parameters but has no StartProcess
   *  trigger anywhere; sidebar shows a warning icon next to these. */
  untriggeredProcessIds?: Record<string, true>;
  /** Invoked when the user clicks the warning icon's "Yes, generate start
   *  screen" affordance for an untriggered process. */
  onConfirmGenerateStartScreen?: (workflowId: string) => void;
}

export interface IStepScreenFormSidebarState {
  activeCollapseKeys: string[];
  isUnlinkScreenModalOpen: boolean;
  stepToBeUnlinked: { processId: string; stepId: string; screenId: string } | null;
  /** Which process's untriggered warning is showing its confirm, if any. */
  openUntriggeredConfirmId: string | null;
}

/** What a step's row says about its screen, and which glyph goes with it. */
interface IStepScreenLabel {
  text: string;
  state: StepScreenState;
}

class StepScreenFormWizardSidebar extends React.PureComponent<
  IStepScreenFormSidebarProps & IWithSDProps,
  IStepScreenFormSidebarState
> {
  constructor(props) {
    super(props);
    this.state = {
      activeCollapseKeys: [],
      isUnlinkScreenModalOpen: false,
      stepToBeUnlinked: null,
      openUntriggeredConfirmId: null
    };
  }

  componentDidMount() {
    if (
      this.props.processes.length === 1 &&
      this.state.activeCollapseKeys.indexOf(`process-${this.props.processes?.[0]?.id}`) === -1
    ) {
      this.setState({ activeCollapseKeys: [`process-${this.props.processes[0].id}`] });
    }
  }

  getProcesses = (): IWorkFlow[] => {
    return _.orderBy(this.props.processes ?? [], ["displayName", "name"], ["asc"]);
  };

  getStepsOfProcess = (process: IWorkFlow): IWFStep[] => {
    const getAllStepsRecursively = (processNodes: IWFNodes): IWFStep[] => {
      const tempSteps: IWFStep[] = [];

      // Get steps from current process
      const currentSteps =
        processNodes.steps?.filter((step: IWFStep) => step.stepType === EWFStepType.ScreenInteraction) ?? [];
      tempSteps.push(...currentSteps);

      // Get steps from subprocesses recursively
      const subProcessActivities = processNodes.steps.filter((sub) => sub.stepType === EWFStepType.Subprocess) ?? [];
      if (subProcessActivities.length > 0) {
        for (const subProcessActivity of subProcessActivities) {
          if (subProcessActivity.subprocess) {
            const subProcessSteps = getAllStepsRecursively(subProcessActivity.subprocess);
            tempSteps.push(...subProcessSteps);
          }
        }
      }

      return tempSteps;
    };

    const allSteps: IWFStep[] = process.nodes ? getAllStepsRecursively(process.nodes) : [];

    return allSteps.sort((a: IWFStep, b: IWFStep) => {
      const nameA = a.name?.trim();
      const nameB = b.name?.trim();

      if (!nameA && !nameB) return 0;
      if (!nameA) return 1;
      if (!nameB) return -1;

      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });
  };

  /**
   * An unnamed user task cannot be told apart in the rail, so the rail says so — once, in
   * place. This used to be an antd `message` fired from inside the render path, which only
   * stayed bearable because antd deduplicated it by key.
   */
  getHasStepWithoutName = (): boolean => {
    return this.getProcesses().some((process: IWorkFlow) =>
      this.getStepsOfProcess(process).some((step: IWFStep) => !step.name?.trim())
    );
  };

  onCollapseChange = (keys: React.Key[]): void => {
    this.setState({ activeCollapseKeys: keys.map((key: React.Key): string => String(key)) });
  };

  renderFolderHeader = (folderName: string, process?: IWorkFlow): React.ReactNode => {
    const isUntriggered = !!process?.id && !!this.props.untriggeredProcessIds?.[process.id];

    return (
      <div className="kuika-step-screen-rail__process">
        <KuikaSvg name="workflow-managed" type="workflow-icons" width={14} height={14} color="var(--dark-7)" />
        <span className="kuika-step-screen-rail__process-name" title={folderName}>
          {folderName}
        </span>
        {isUntriggered && process && this.renderUntriggeredWarning(process)}
      </div>
    );
  };

  onConfirmGenerateStartScreen = (process: IWorkFlow): void => {
    this.setState({ openUntriggeredConfirmId: null });
    this.props.onConfirmGenerateStartScreen?.(process.id);
  };

  renderUntriggeredWarning = (process: IWorkFlow): React.ReactNode => {
    const confirmBody: React.ReactNode = (
      <div className="kuika-step-screen-rail__confirm">
        <span>
          This process takes input parameters and is not started from anywhere in this project. Generate a start screen
          for it?
        </span>
        <span className="kuika-flex-end--gap">
          <KuikaButton
            type="tertiary"
            color="neutral"
            size="sm"
            onClick={(): void => this.setState({ openUntriggeredConfirmId: null })}
          >
            No
          </KuikaButton>
          <KuikaButton size="sm" onClick={(): void => this.onConfirmGenerateStartScreen(process)}>
            Yes
          </KuikaButton>
        </span>
      </div>
    );

    return (
      <KuikaPopover
        position="bottom"
        align="start"
        content={confirmBody}
        visible={this.state.openUntriggeredConfirmId === process.id}
        onVisibleChange={(visible: boolean): void =>
          this.setState({ openUntriggeredConfirmId: visible ? process.id : null })
        }
      >
        <KuikaButton
          type="tertiary"
          color="neutral"
          size="sm"
          isIcon
          aria-label="No StartProcess action targets this process"
          onClick={(e: React.MouseEvent<HTMLButtonElement>): void => e.stopPropagation()}
          data-testid={`untriggeredProcessWarning-${process.id}`}
        >
          <KuikaSvg name="alert-triangle" width={14} height={14} color="var(--orange-6)" />
        </KuikaButton>
      </KuikaPopover>
    );
  };

  onClickStep = (process: IWorkFlow, step: IWFStep) => {
    if (!process?.id || !step?.id) {
      return;
    }

    if (process.id === this.props.selectedProcessId && step.id === this.props.selectedStepId) {
      return;
    }

    this.props.onChangeSelectedStep(process.id, step.id);
  };

  onClickUnlinkScreen = (process: IWorkFlow, step: IWFStep) => {
    if (!process?.id || !step?.id) {
      return;
    }

    const stepScreen: IScreenDesignInfo | undefined = this.props.getScreenForStep(process.id, step.id);

    if (!stepScreen) {
      return;
    }

    this.setState({
      isUnlinkScreenModalOpen: true,
      stepToBeUnlinked: {
        processId: process.id,
        stepId: step.id,
        screenId: stepScreen.id
      }
    });
  };

  getIsStepInDraft = (processId: string, stepId: string) => {
    return !!this.props.draftSteps.find((draft) => draft.processId === processId && draft.stepId === stepId);
  };

  getPendingScreenForStep = (processId: string, stepId: string): IScreenDesignInfo | undefined => {
    const pending = this.props.pendingUnmanagedAssignments?.find(
      (p) => p.processId === processId && p.stepId === stepId
    );
    if (!pending) return undefined;
    return this.props.sd.screenList?.find((s: IScreenDesignInfo) => s.id === pending.screenId);
  };

  /** The screen a step is bound to, in the words and the state the rail draws it with. */
  getStepScreenLabel = (process: IWorkFlow, step: IWFStep): IStepScreenLabel => {
    const stepScreen: IScreenDesignInfo | undefined = this.props.getScreenForStep(process.id, step.id);

    if (stepScreen) {
      const isManaged: boolean = this.props.getIsScreenManaged(process.id, step.id);
      return { text: `Screen: ${stepScreen.name}`, state: isManaged ? "managed" : "unmanaged" };
    }

    if (this.getIsStepInDraft(process.id, step.id)) {
      return { text: "Screen: Draft Screen", state: "draft" };
    }

    const pendingScreen: IScreenDesignInfo | undefined = this.getPendingScreenForStep(process.id, step.id);

    if (pendingScreen) {
      return { text: `Screen: ${pendingScreen.name}`, state: "pending" };
    }

    return { text: "Screen: No Screen", state: "none" };
  };

  renderStepButton = (process: IWorkFlow, step: IWFStep): React.ReactNode => {
    const label: IStepScreenLabel = this.getStepScreenLabel(process, step);
    const icon: IStepScreenStateIcon = STEP_SCREEN_STATE_ICON[label.state];
    const isSelected: boolean = process.id === this.props.selectedProcessId && step.id === this.props.selectedStepId;
    const unlinkOptions: KuikaDropdownMenuItemType[] = [
      {
        key: "unlink-screen",
        label: "Unlink Screen",
        danger: true,
        onClick: (): void => this.onClickUnlinkScreen(process, step)
      }
    ];

    return (
      <KuikaMenuItem
        key={`${process.id}-${step.id}`}
        title={step.name || "Untitled step"}
        subtitle={label.text}
        selected={isSelected}
        role="button"
        tabIndex={0}
        aria-current={isSelected ? "true" : undefined}
        data-testid={`workflowScreensStep-${step.id}`}
        onClick={(): void => this.onClickStep(process, step)}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>): void => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.onClickStep(process, step);
          }
        }}
        rightUnit={
          <span title={icon.title}>
            <KuikaSvg name={icon.name} width={16} height={16} color={icon.color} />
          </span>
        }
        rightDropdown={
          this.props.getIsScreenManaged(process.id, step.id) ? (
            <KuikaDropdownMenu options={unlinkOptions} placement="bottomRight">
              <KuikaButton
                type="tertiary"
                color="neutral"
                size="sm"
                isIcon
                aria-label="Step screen actions"
                onClick={(e: React.MouseEvent<HTMLButtonElement>): void => e.stopPropagation()}
                data-testid={`workflowScreensStepActions-${step.id}`}
              >
                <KuikaSvg name="dots" width={16} height={16} />
              </KuikaButton>
            </KuikaDropdownMenu>
          ) : undefined
        }
      />
    );
  };

  onCloseUnlinkScreenModal = (isUnlinked: boolean) => {
    if (!this.state.stepToBeUnlinked) {
      return;
    }

    if (isUnlinked) {
      this.props.removeDraftFormMetadata(this.state.stepToBeUnlinked.processId, this.state.stepToBeUnlinked.stepId);
      if (
        this.props.selectedProcessId === this.state.stepToBeUnlinked.processId &&
        this.props.selectedStepId === this.state.stepToBeUnlinked.stepId
      ) {
        this.props.setScreenUnmanaged(this.state.stepToBeUnlinked.processId, this.state.stepToBeUnlinked.stepId);
      }
    }

    this.setState({ isUnlinkScreenModalOpen: false, stepToBeUnlinked: null }, () => {});
  };

  render() {
    const isOnLandingPage: boolean = !this.props.selectedProcessId && !this.props.selectedStepId;

    return (
      <div className="kuika-step-screen-rail">
        {this.getHasStepWithoutName() && (
          <KuikaInfoBox
            className="kuika-step-screen-rail__notice"
            color="yellow"
            leftUnit="The 'name' field of some User Task steps is empty. Please give names to all steps."
          />
        )}
        {/* Get Started shortcut aligned like steps */}
        <KuikaMenuItem
          title="Get Started"
          subtitle="Open landing page"
          selected={isOnLandingPage}
          role="button"
          tabIndex={0}
          data-testid="workflowScreensGetStarted"
          onClick={(): void => {
            // Only trigger if not already on landing page
            if (this.props.selectedProcessId || this.props.selectedStepId) {
              this.props.onClickGetStarted?.();
            }
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>): void => {
            if ((e.key === "Enter" || e.key === " ") && !isOnLandingPage) {
              e.preventDefault();
              this.props.onClickGetStarted?.();
            }
          }}
        />
        <KuikaCollapsePanel
          className="kuika-step-screen-rail__processes"
          activeKey={this.state.activeCollapseKeys}
          onChange={this.onCollapseChange}
          items={this.getProcesses().map((process: IWorkFlow) => ({
            key: `process-${process.id}`,
            label: this.renderFolderHeader(process.displayName ?? process.name, process),
            children: this.getStepsOfProcess(process).map((step: IWFStep) => this.renderStepButton(process, step))
          }))}
        />
        {this.state.isUnlinkScreenModalOpen && this.state.stepToBeUnlinked && (
          <StepScreenModalUnlinkScreenModal
            isOpen={this.state.isUnlinkScreenModalOpen}
            onClose={this.onCloseUnlinkScreenModal}
            step={this.state.stepToBeUnlinked}
            reloadScreen={this.props.reloadScreen}
          />
        )}
      </div>
    );
  }
}

const stepScreenFormWizardSidebar = withSD<IStepScreenFormSidebarProps>(StepScreenFormWizardSidebar);
export { stepScreenFormWizardSidebar as StepScreenFormWizardSidebar };
